// =====================================================================================
// INVRT Radar — web console — one site — Plan tab
//
// The site's plan as GET /sites/:id/plan returns it after the C1 scrub (docs/
// API-CONTRACTS-C1.md §1, §8, §10): the fields the tenant owns and nothing else — plan
// type, trial state and end date, this month's identifications against the monthly
// budget, and the status. Every figure is shown as held; nothing here adds, subtracts or
// projects.
//
// The one figure that does not come from the plan route is the free-identification
// allowance, which GET /sites/:id merges into the Site row (`free_results_remaining`,
// _shared/vv_trial.ts). lib/types.ts's Site does not declare it, so this file reads it
// defensively from the row it is handed: present → shown, absent → not mentioned. The
// trial sentence follows the same rule — it names only the legs the server has stated.
//
// Activate and cancel are organisation-admin actions on the server (requireOrgAdmin) and
// are gated the same way here. Both are bodiless POSTs; both re-read the plan and the site
// afterwards rather than guessing at the new state.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { formatDate, humaniseAction } from "@/lib/format";
import type { Site } from "@/lib/types";
import type {
  ActivateSitePlanResponse,
  CancelSitePlanResponse,
  SitePlan,
} from "@/lib/types.c1";

/**
 * What GET /sites/:id/plan can put on the wire for `plan_type`: the contract names the two
 * tenant words, and the handler answers null for a site whose plan type is not yet known
 * (tenantPlanType() in routes/sites.ts). The mirror in types.c1.ts omits the null, so it
 * is widened locally rather than trusted.
 */
type SitePlanOnTheWire = Omit<SitePlan, "plan_type"> & { plan_type: SitePlan["plan_type"] | null };

/** Reads the free-allowance figure GET /sites/:id merges into the row, if it is there. */
function freeResultsRemaining(site: Site): number | null {
  const value = (site as Site & { free_results_remaining?: unknown }).free_results_remaining;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function planTypeLabel(planType: SitePlanOnTheWire["plan_type"]): string {
  if (planType === "b2b") return "Business visitors";
  if (planType === "b2c") return "Consumer visitors";
  return "Not set yet";
}

function statusBadge(status: string): { label: string; tone: "info" | "success" | "neutral" } {
  switch (status) {
    case "trial":
      return { label: "On trial", tone: "info" };
    case "active":
      return { label: "Active", tone: "success" };
    case "cancelled":
      return { label: "Cancelled", tone: "neutral" };
    case "pending":
      return { label: "Pending", tone: "neutral" };
    default:
      return { label: humaniseAction(status), tone: "neutral" };
  }
}

/**
 * The trial sentence, built only from facts the server has actually stated: the end date
 * from the plan route, the free allowance from the site row. Neither → no sentence.
 */
function trialSentence(endsAt: string | null, hasFreeAllowance: boolean): string | null {
  if (endsAt && hasFreeAllowance) {
    return `The trial ends on ${formatDate(endsAt)} or when the free identifications are used, whichever comes first.`;
  }
  if (endsAt) return `The trial ends on ${formatDate(endsAt)}.`;
  if (hasFreeAllowance) return "The trial ends when the free identifications are used.";
  return null;
}

export function PlanPanel({ site }: { site: Site }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId, activeMembership } = useSession();
  const { push } = useToast();
  const [confirming, setConfirming] = useState<"activate" | "cancel" | null>(null);

  const planKey = ["site-plan", orgId, site.id];
  const plan = useQuery({
    queryKey: planKey,
    queryFn: () => api.get<SitePlanOnTheWire>(`/sites/${site.id}/plan`),
    enabled: Boolean(orgId),
  });

  const afterChange = () => {
    void queryClient.invalidateQueries({ queryKey: planKey });
    void queryClient.invalidateQueries({ queryKey: ["site", orgId, site.id] });
    void queryClient.invalidateQueries({ queryKey: ["sites", orgId] });
    setConfirming(null);
  };

  const activate = useMutation({
    mutationFn: () => api.post<ActivateSitePlanResponse>(`/sites/${site.id}/plan/activate`),
    onSuccess: () => {
      afterChange();
      push({
        tone: "success",
        title: "Plan activated",
        body: "Billing for this site starts from now at the rates on the pricing page.",
      });
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post<CancelSitePlanResponse>(`/sites/${site.id}/plan/cancel`),
    onSuccess: () => {
      afterChange();
      push({
        tone: "info",
        title: "Plan cancelled",
        body: "Billing for this site has stopped. The site and its settings remain.",
      });
    },
  });

  const isAdmin = activeMembership?.is_org_admin === true;

  if (plan.isLoading) return <LoadingState label="Loading this site's plan…" />;
  if (plan.isError) return <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />;
  if (!plan.data) return null;

  const data = plan.data;
  const status = statusBadge(data.status);
  const onTrial = data.trial.state === "trial";
  const freeRemaining = freeResultsRemaining(site);
  const sentence = onTrial ? trialSentence(data.trial.ends_at, freeRemaining !== null) : null;
  const canActivate = data.status !== "active";
  const canCancel = data.status === "active";

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Plan</CardTitle>
            <Badge tone={status.tone}>{status.label}</Badge>
          </div>
          <p className="text-sm text-ink-muted">
            Your plan, trial and monthly identifications for {site.domain}, read from
            Radar&rsquo;s own records.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Plan type
              </dt>
              <dd className="mt-1 text-sm font-medium text-ink">{planTypeLabel(data.plan_type)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Trial end date
              </dt>
              <dd className="mt-1 text-sm font-medium text-ink">
                {data.trial.ends_at ? formatDate(data.trial.ends_at) : "Not set"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Identifications this month
              </dt>
              <dd className="mt-1 flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-ink">
                  {data.identifications.month_to_date.toLocaleString("en-GB")}
                </span>
                <span className="text-sm text-ink-muted">
                  {data.identifications.monthly_budget === null
                    ? "No monthly limit"
                    : `of ${data.identifications.monthly_budget.toLocaleString("en-GB")} allowed`}
                </span>
              </dd>
            </div>
            {freeRemaining !== null && onTrial ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Free identifications remaining
                </dt>
                <dd className="mt-1 text-sm font-medium text-ink">
                  {freeRemaining.toLocaleString("en-GB")}
                </dd>
              </div>
            ) : null}
          </dl>

          {sentence ? <p className="text-sm text-ink">{sentence}</p> : null}

          {isAdmin ? (
            <div className="flex flex-wrap items-center gap-3">
              {canActivate ? (
                <Button onClick={() => setConfirming("activate")}>Activate plan</Button>
              ) : null}
              {canCancel ? (
                <Button variant="secondary" onClick={() => setConfirming("cancel")}>
                  Cancel plan
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">
              An administrator on your team can activate or cancel the plan for this site.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={confirming === "activate"}
        onClose={() => setConfirming(null)}
        title="Activate the paid plan?"
        description={
          onTrial
            ? `This ends the trial for ${site.domain} now and starts billing at the rates on the pricing page.`
            : `This starts billing for ${site.domain} at the rates on the pricing page.`
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              {onTrial ? "Keep the trial" : "Not now"}
            </Button>
            <Button
              variant="commit"
              loading={activate.isPending}
              onClick={() => activate.mutate()}
            >
              Activate and start billing
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-muted">
            Identifications are charged per the rates in force for your organisation.{" "}
            <Link
              to="/account?tab=pricing"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              See the pricing page
            </Link>
            .
          </p>
          {activate.isError ? <ErrorState error={activate.error} /> : null}
        </div>
      </Dialog>

      <Dialog
        open={confirming === "cancel"}
        onClose={() => setConfirming(null)}
        title="Cancel the paid plan?"
        description={`Billing for ${site.domain} stops. The site, its pixel and its settings stay in Radar.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Keep the plan
            </Button>
            <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancel the plan
            </Button>
          </>
        }
      >
        {cancel.isError ? <ErrorState error={cancel.error} /> : null}
      </Dialog>
    </div>
  );
}
